import styled from 'styled-components';
import { cssVars } from '../../../shared/lib/styledTheme';

export const TicketDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 0;
  background-color: ${cssVars.bgSecondary};
  border-left: 1px solid ${cssVars.borderColor};
  overflow-y: auto;
  overflow-x: hidden;

  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  
  scrollbar-width: thin;
  scrollbar-color: ${cssVars.borderColor} transparent;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${cssVars.borderColor};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${cssVars.textSecondary};
  }
`;

export const Section = styled.div`
  padding: 16px 12px;
  border-bottom: 1px solid ${cssVars.borderColor};
  background-color: ${cssVars.bgSecondary};
  min-width: 0;

  @media (max-width: 400px) {
    padding: 12px 8px;
  }
`;

export const ClientHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  min-width: 0;

  @media (max-width: 400px) {
    gap: 8px;
    margin-bottom: 8px;
  }
`;

export const ClientIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${cssVars.primary} 0%, ${cssVars.primaryHover} 100%);
  color: ${cssVars.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 2px 8px ${cssVars.primary}30;

  @media (max-width: 400px) {
    width: 36px;
    height: 36px;
    font-size: 16px;
    border-radius: 8px;
  }
`;

export const ClientInfo = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const ClientName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${cssVars.textPrimary};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 400px) {
    font-size: 13px;
  }
`;

export const ClientTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: ${cssVars.primary};
  color: ${cssVars.white};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-top: 4px;
`;

export const UID = styled.div`
  font-size: 13px;
  color: ${cssVars.textSecondary};
  margin-top: 8px;
`;

export const SectionTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${cssVars.textSecondary};
  letter-spacing: 0.8px;
  margin-bottom: 8px;
  opacity: 0.8;
  word-break: break-word;

  @media (max-width: 400px) {
    font-size: 9px;
    margin-bottom: 6px;
  }
`;

export const SectionContent = styled.div`
  font-size: 13px;
  color: ${cssVars.textPrimary};
  line-height: 1.5;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 400px) {
    font-size: 12px;
    line-height: 1.4;
  }
`;

export const StatusIndicator = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.$active ? '#4CAF50' : cssVars.textSecondary};
`;

export const StatusDot = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$active ? '#4CAF50' : cssVars.textSecondary};
`;

export const SiteBadge = styled.span<{ $site: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${props => props.$site === 'hudle' ? '#4CAF50' : '#2196F3'};
  color: ${cssVars.white};
`;

export const AdminToolsSection = styled.div`
  padding: 10px 12px;
  background-color: ${cssVars.bgColor};
  border-bottom: 1px solid ${cssVars.borderColor};

  @media (max-width: 400px) {
    padding: 8px 10px;
  }
`;

export const AdminSectionTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${cssVars.textSecondary};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.8;

  @media (max-width: 400px) {
    font-size: 9px;
    margin-bottom: 6px;
  }
`;

export const AdminButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 10px;
  background-color: ${cssVars.bgSecondary};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 8px;
  font-size: 14px;
  color: ${cssVars.textPrimary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background-color: ${cssVars.hover};
    border-color: ${cssVars.primary};
    transform: translateX(2px);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 12px;
`;

export const IntegrationButton = styled.button`
  aspect-ratio: 1;
  padding: 20px;
  background-color: ${cssVars.bgSecondary};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${cssVars.hover};
    border-color: ${cssVars.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

export const IntegrationIcon = styled.div`
  font-size: 24px;
`;

export const IntegrationLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${cssVars.textPrimary};
  text-align: center;
`;

export const KnowledgeBaseLink = styled.a`
  display: block;
  padding: 8px 0;
  color: ${cssVars.primary};
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;

  &:hover {
    color: ${cssVars.primaryHover};
    text-decoration: underline;
  }
`;

export const AuditHistoryContainer = styled.div`
  padding: 16px 12px;

  @media (max-width: 400px) {
    padding: 12px 8px;
  }
`;

export const AuditEvent = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${cssVars.borderLight};
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 400px) {
    padding: 8px 0;
  }
`;

export const AuditEventDescription = styled.div`
  font-size: 12px;
  color: ${cssVars.textPrimary};
  margin-bottom: 4px;
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 400px) {
    font-size: 11px;
    margin-bottom: 3px;
  }
`;

export const AuditEventMeta = styled.div`
  font-size: 10px;
  color: ${cssVars.textTertiary};

  @media (max-width: 400px) {
    font-size: 9px;
  }
`;

export const EmptyStateContainer = styled.div`
  padding: 20px;
  color: ${cssVars.textSecondary};
  text-align: center;
`;

export const SectionWrapper = styled.div`
  margin-bottom: 24px;
`;

export const SectionTitleWithMargin = styled(SectionTitle)`
  margin-bottom: 12px;
`;

export const EmptySectionContent = styled(SectionContent)`
  color: ${cssVars.textTertiary};
  font-size: 13px;
`;

export const CompactToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;

  @media (max-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2px;
  }
`;

export const CompactToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 6px;
  background-color: ${cssVars.bgSecondary};
  border: 1px solid ${cssVars.borderColor};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;
  min-height: 28px;

  &:hover {
    background-color: ${cssVars.hover};
    border-color: ${cssVars.primary};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 400px) {
    padding: 4px 5px;
    min-height: 26px;
  }
`;

export const CompactToolLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${cssVars.textPrimary};
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;

  @media (max-width: 400px) {
    font-size: 9px;
  }
`;
